import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('jump-avatar')
export class Avatar extends LitElement {
  @property({ type: String, attribute: 'src' }) src: string | null = null
  @property({ type: String }) size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md'
  @property({ type: String }) class: string = ''
  @property({ type: String }) name: string | null = null
  @property({ type: Boolean }) randomColor: boolean = false
  @property({ type: Array }) avatars: string[] = []

  static readonly avatarColors: Array<string> = [
    '#99f0a1', // avatar-dataviz-01
    '#9bcde8', // avatar-dataviz-02
    '#b3b3f9', // avatar-dataviz-03
    '#ffdf90', // avatar-dataviz-04
    '#5aaad5', // avatar-dataviz-05
    '#c6cdd2', // avatar-dataviz-06
    '#edb1bc', // avatar-dataviz-07
  ]

  private srcBlank(src: string | null): boolean {
    return !src || src === ''
  }

  private maybeGenerateRandomColor(randomColor: boolean, name: string | null): string | null {
    if (randomColor && name) {
      return `background-color: ${this.generateColorFromString(name)}; color: #09090b;`
    }
    return null
  }

  private generateColorFromString(name: string): string {
    const hash = name.split('').reduce((hash, char) => {
      hash = (hash << 5) - hash + char.charCodeAt(0)
      return hash & hash
    }, 0)

    const colorIndex = Math.abs(hash) % Avatar.avatarColors.length
    return Avatar.avatarColors[colorIndex]
  }

  private generateInitials(name: string | null): string {
    if (!name) return ''

    const cleanName = name.replace(/\s*\([^)]*\)/g, '')

    const wordArray = cleanName.split(' ')

    if (wordArray.length === 1) {
      return wordArray[0].slice(0, 2).toUpperCase()
    } else {
      const initial1 = wordArray[0].charAt(0)
      const initial2 = wordArray[wordArray.length - 1].charAt(0)
      return (initial1 + initial2).toUpperCase()
    }
  }

  render() {
    return html`
      ${this.srcBlank(this.src) && !this.name
        ? html`
            <div class="pc-avatar--with-placeholder-icon pc-avatar--${this.size} ${this.class}">
              <svg class="pc-avatar__placeholder-icon">
                <path
                  fill-rule="evenodd"
                  d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          `
        : this.srcBlank(this.src) && this.name
          ? html`
              <div
                style=${this.maybeGenerateRandomColor(this.randomColor, this.name)}
                class="pc-avatar--with-placeholder-initials pc-avatar--${this.size} ${this.class}"
              >
                ${this.generateInitials(this.name)}
              </div>
            `
          : html` <img src=${this.src} class="pc-avatar--with-image pc-avatar--${this.size} ${this.class}" /> `}
    `
  }

  static styles = css`
    .pc-avatar {
      display: inline-block;
      vertical-align: middle;
    }

    .pc-avatar--with-placeholder-icon {
      background-color: #f0f0f0; /* Example background color */
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .pc-avatar--with-placeholder-icon svg {
      width: 1.25rem; /* Adjust size as needed */
      height: 1.25rem; /* Adjust size as needed */
      fill: #666; /* Example fill color for the icon */
    }

    .pc-avatar--with-placeholder-initials {
      background-color: #007bff; /* Example background color */
      color: white; /* Example text color */
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem; /* Adjust font size as needed */
    }

    .pc-avatar--with-image {
      display: flex;
      object-fit: cover;
      border-radius: 50%;
      width: 100%; /* Ensures the image fills the container */
      height: auto; /* Maintains aspect ratio */
    }

    .pc-avatar--xs {
      height: 1.25rem;
      width: 1.25rem;
      font-size: 10px;
    }
    .pc-avatar--tiny {
      height: 1.5rem;
      width: 1.5rem;
      font-size: 12px;
    }

    .pc-avatar--sm {
      height: 2rem;
      width: 2rem;
      font-size: 13px;
    }

    .pc-avatar--md {
      height: 2.5rem;
      width: 2.5rem;
      font-size: 15px;
    }

    .pc-avatar--lg {
      height: 3rem;
      width: 3rem;
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    .pc-avatar--xl {
      height: 3.5rem;
      width: 3.5rem;
      font-size: 1.25rem;
      line-height: 1.75rem;
    }

    .pc-avatar--2xl {
      height: 4rem;
      width: 4rem;
      font-size: 1.5rem;
      line-height: 2rem;
    }
  `
}
